import React, { useEffect, useRef, useState } from "react";

import { useBagApi } from "@/api/bag-query";
import { useCategories } from "@/api/category-query";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/store/auth";
import { useBagStore } from "@/store/bag";
import { useNavigate, useRouter } from "@tanstack/react-router";

export interface TabType {
	id: string;
	title: string;
}

interface NavbarRefundProps {
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	onProfileClick?: (e: React.MouseEvent) => void;
	onBagClick?: (e: React.MouseEvent) => void;
	activeTab?: TabType;
	setActiveTab?: (tab: TabType) => void;
	tabs?: TabType[];
	hideOnScroll?: boolean; // new prop
}

const NavbarRefund: React.FC<NavbarRefundProps> = ({ searchValue, onSearchChange, onProfileClick, onBagClick, activeTab, setActiveTab, tabs, hideOnScroll = true }) => {
	const [showNavbar, setShowNavbar] = useState(true);
	const lastScrollY = useRef(0);
	const isAuthenticated = useAuthStore(state => state.isAuthenticated);
	const { getCartBags } = useBagApi();
	const [authBagCount, setAuthBagCount] = useState(0);

	// Get bag item count for guest users
	const bagItems = useBagStore(state => state.items);
	const guestBagCount = bagItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

	// Fetch authenticated bag count
	useEffect(() => {
		if (isAuthenticated) {
			getCartBags()
				.then((data) => {
					const count = data?.cartBags?.reduce((sum: number, bag: any) => sum + (bag.items?.length || 0), 0) || 0;
					setAuthBagCount(count);
				})
				.catch(() => {
					setAuthBagCount(0);
				});
		}
	}, [isAuthenticated, getCartBags]);

	// Use authenticated bag count if logged in, otherwise use guest count
	const bagCount = isAuthenticated ? authBagCount : guestBagCount;
	useEffect(() => {
		if (!hideOnScroll) {
			setShowNavbar(true);
			return;
		}
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const threshold = 100;
			if (currentScrollY <= threshold) {
				setShowNavbar(true);
				lastScrollY.current = currentScrollY;
				return;
			}
			if (currentScrollY > lastScrollY.current) {
				setShowNavbar(false);
			}
			else if (currentScrollY < lastScrollY.current) {
				setShowNavbar(true);
			}
			lastScrollY.current = currentScrollY;
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [hideOnScroll]);

	const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
	const [internalActiveTab, internalSetActiveTab] = useState<TabType>({ id: "All", title: "All" });
	const tabList = React.useMemo(() => {
		if (Array.isArray(tabs) && tabs.length > 0)
			return tabs;
		if (!categoriesLoading && categoriesData?.data?.length) {
			return [{ id: "All", title: "All" }, ...categoriesData.data.map((cat: any) => ({ id: cat.id, title: cat.title }))];
		}
		return [{ id: "All", title: "All" }];
	}, [tabs, categoriesData, categoriesLoading]);
	const tab = activeTab ?? internalActiveTab;
	const setTab = setActiveTab ?? internalSetActiveTab;

	// Track scroll position of category scrollbar for fade indicators
	const categoryScrollRef = useRef<HTMLDivElement>(null);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollIndicators = () => {
		const el = categoryScrollRef.current;
		if (!el)
			return;
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	};

	useEffect(() => {
		const el = categoryScrollRef.current;
		if (!el)
			return;
		updateScrollIndicators();
		el.addEventListener("scroll", updateScrollIndicators);
		window.addEventListener("resize", updateScrollIndicators);
		return () => {
			el.removeEventListener("scroll", updateScrollIndicators);
			window.removeEventListener("resize", updateScrollIndicators);
		};
	}, [tabList]);

	// Ensure 'All' is active by default on mount or when tabList changes
	useEffect(() => {
		if (!tab || !tab.id || !tabList.some(t => t.id === tab.id)) {
			setTab(tabList[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabList]);
	const [search, setSearch] = useState(searchValue || "");
	const navigate = useNavigate();
	const router = useRouter();

	// Use onProfileClick from props if provided
	const handleProfileClick = onProfileClick || ((e: React.MouseEvent) => {
		e.preventDefault();
	});

	// Logo click: refresh if already on /discovery
	const handleLogoClick = () => {
		if (router.state.location.pathname === "/discovery") {
			window.location.reload();
		}
		else {
			navigate({ to: "/discovery" });
		}
	};

	useEffect(() => {
		if (typeof searchValue === "string" && searchValue !== search) {
			setSearch(searchValue);
		}
	}, [searchValue, search]);

	// Search action handler
	const handleSearchAction = () => {
		if (onSearchChange)
			onSearchChange(search);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
		if (onSearchChange) {
			onSearchChange(e.target.value);
		}
	};

	return (
		<>
			{/* Navbar */}
			<nav
				className={`bg-gradient-to-b from-[#3F2EC8] to-[#1F1762] px-2 sm:px-4 pt-4 pb-2 flex flex-col shadow transition-transform duration-300 ease-in-out ${showNavbar ? "translate-y-0" : "-translate-y-full"} fixed top-0 left-0 w-full z-50`}
				style={{ minHeight: 80 }}
			>
				{/* Top Row: Always horizontal (logo, search, icons) and properly aligned */}
				<div className="flex flex-row items-center justify-between w-full px-2 sm:px-6 gap-2 min-h-[56px]">
					{/* Logo */}
					<div className="flex-shrink-0 flex items-center">
						<Button
							variant="ghost"
							size="icon"
							tabIndex={0}
							aria-label="Intasida Logo Button"
							title="Discovery Page"
							className="p-0 rounded-none hover:bg-transparent focus:bg-transparent"
							onClick={handleLogoClick}
						>
							<img src="/intasida-icon.svg" alt="Logo" className="w-8 h-10" />
						</Button>
					</div>

					{/* Search Bar */}
					<div className="flex-1 flex items-center justify-center min-w-0 mx-2">
						<div className="relative flex items-center border border-[#B3B3B3] focus-within:border-white rounded-lg w-full max-w-md h-10 transition-colors px-2 bg-transparent">
							<input
								type="text"
								placeholder="Search..."
								value={search}
								onChange={handleSearchChange}
								onKeyDown={(e) => {
									if (e.key === "Escape") {
										if (search) {
											setSearch("");
											if (onSearchChange)
												onSearchChange("");
											// Keep focus
										}
										else {
											e.currentTarget.blur();
										}
									}
									if (e.key === "Enter") {
										handleSearchAction();
										e.currentTarget.blur();
									}
								}}
								className="flex-1 bg-transparent text-[#D9D9D9] text-sm leading-[120%] font-normal font-['Inter'] focus:outline-none h-10 min-w-0"
								style={{ border: "none" }}
							/>
							<div className="flex items-center gap-0">
								{search && (
									<button
										type="button"
										tabIndex={0}
										aria-label="Clear search"
										title="Clear search"
										onMouseDown={e => e.preventDefault()}
										onClick={(_e) => {
											setSearch("");
											if (onSearchChange)
												onSearchChange("");
											// Do not blur input, keep focus
										}}
										className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[#1F1762] transition-colors bg-transparent border-0 p-0 m-0 flex items-center justify-center h-8 w-8 hover:bg-[#3f2ec8] hover:text-black"
										style={{ lineHeight: 1 }}
									>
										<img src="/assets/icons/close.svg" alt="Cancel" className="h-4 w-4 pointer-events-none" style={{ filter: "invert(90%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%)", color: "#D9D9D9", display: "block" }} />
									</button>
								)}
							</div>
						</div>
					</div>

					{/* Icons */}
					<div className="flex items-center gap-2 flex-shrink-0">
						<Button
							variant="ghost"
							size="icon"
							className="text-white rounded-full p-2"
							style={{ display: "flex", alignItems: "center" }}
							onClick={handleProfileClick}
							aria-label="Profile Button"
							title="Profile"
						>
							<img src="/assets/icons/profile.svg" alt="Profile" className="w-5 h-5" />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="relative text-white rounded-full p-2"
							style={{ display: "flex", alignItems: "center" }}
							onClick={(e) => {
								if (onBagClick) {
									onBagClick(e);
								}
								else {
									navigate({ to: "/bag" });
								}
							}}
							aria-label="Shopping Bag Button"
							title="Bag"
						>
							<img src="/assets/icons/shopping-bag.svg" alt="Shopping Bag" className="w-5 h-5" />
							{bagCount > 0 && (
								<span
									className="absolute"
									aria-label={`Bag contains ${bagCount} item${bagCount > 1 ? "s" : ""}`}
									style={{
										top: "2.5px",
										left: "22px", // 11px from left of icon, icon is 20px wide, so adjust for overlap
										width: "12px",
										height: "12px",
										background: "#CA2222",
										color: "#FFF",
										borderRadius: "6px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontFamily: "Inter, sans-serif",
										fontWeight: 400,
										fontSize: "8px",
										lineHeight: "120%",
										padding: "1px 3px",
										zIndex: 2,
										boxSizing: "border-box"
									}}
								>
									{bagCount}
								</span>
							)}
						</Button>
					</div>
				</div>
				{/* Category Links — edge-to-edge */}
				<div className="relative mt-4 mb-2 flex items-center -mx-2 sm:-mx-4">
					{/* Fixed 'All' button */}
					<div className="flex-shrink-0 pl-3 sm:pl-5">
						<Button
							key="All"
							variant={tab?.id === "All" ? "default" : "ghost"}
							onClick={() => {
								setTab({ id: "All", title: "All" });
								handleSearchAction();
								// Blur any focused element inside the search bar to remove focus-within styling
								const searchBar = document.querySelector(".relative.flex.items-center");
								if (searchBar) {
									const focused = searchBar.querySelector(":focus");
									if (focused && focused instanceof HTMLElement)
										focused.blur();
								}
							}}
							className={`relative px-2 sm:px-3 pb-2 mb-1 whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[#1F1762] ${
								tab?.id === "All"
									? "font-semibold text-[#fff]"
									: "text-[#B3B3B3] hover:text-[#fff]"
							}`}
							style={{
								fontFamily: "Inter, sans-serif",
								fontWeight: 400,
								fontStyle: "normal",
								fontSize: 14,
								lineHeight: "120%",
								letterSpacing: 0,
								position: "relative"
							}}
						>
							All
							{tab?.id === "All" && (
								<span style={{
									display: "block",
									position: "absolute",
									left: 0,
									right: 0,
									bottom: 0,
									height: 3,
									borderRadius: 2,
									background: "#fff"
								}}
								/>
							)}
						</Button>
					</div>
					{/* Scrollable categories except 'All' */}
					<div
						ref={categoryScrollRef}
						className="flex-1 overflow-x-auto scrollbar-hide py-1 pl-1 pr-0"
						id="category-scrollbar"
						style={{
							scrollBehavior: "smooth",
							...(canScrollRight
								? {
										maskImage: "linear-gradient(to right, black 0%, black calc(100% - 3rem), transparent 100%)",
										WebkitMaskImage: "linear-gradient(to right, black 0%, black calc(100% - 3rem), transparent 100%)"
									}
								: {})
						}}
					>
						<div className="flex gap-1 min-w-max pr-6">
							{tabList.filter(cat => cat.id !== "All").map(cat => (
								<Button
									key={cat.id}
									variant={tab?.id === cat.id ? "default" : "ghost"}
									onClick={() => {
										setTab({ id: cat.id, title: cat.title });
									}}
									className={`relative px-2 sm:px-3 pb-2 mb-1 whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[#1F1762] focus-visible:z-10 ${
										tab?.id === cat.id
											? "font-semibold text-[#fff]"
											: "text-[#B3B3B3] hover:text-[#fff]"
									}`}
									style={{
										fontFamily: "Inter, sans-serif",
										fontWeight: 400,
										fontStyle: "normal",
										fontSize: 14,
										lineHeight: "120%",
										letterSpacing: 0,
										position: "relative"
									}}
								>
									{cat.title}
									{tab?.id === cat.id && (
										<span style={{
											display: "block",
											position: "absolute",
											left: 0,
											right: 0,
											bottom: 0,
											height: 3,
											borderRadius: 2,
											background: "#fff"
										}}
										/>
									)}
								</Button>
							))}
						</div>
					</div>
				</div>
			</nav>
			{/* Spacer for fixed navbar */}
			<div style={{ height: 155 }} />
			{/* Delivery Guarantee Section */}
			<section
				className="w-full flex justify-center bg-[#E2E1EB]"
				style={{
					minHeight: 42,
					paddingTop: 4,
					paddingBottom: 4,
					paddingLeft: 80,
					paddingRight: 80
				}}
			>
				<div
					className="flex flex-col gap-1 w-full"
					style={{ maxWidth: 1280, minHeight: 34, gap: 6 }}
				>
					<div
						className="flex items-center gap-1.5"
						style={{ minHeight: 14, gap: 6 }}
					>
						<img
							src="/assets/icons/group.svg"
							alt="Delivery Guarantee"
							style={{ width: 14, height: 14, opacity: 1 }}
						/>
						<span
							style={{
								color: "#2A2A2A",
								fontFamily: "Inter, sans-serif",
								fontWeight: 500,
								fontSize: 12,
								lineHeight: "103%",
								letterSpacing: "-0.07em",
								opacity: 1
							}}
						>
							Delivery guarantee
						</span>
					</div>
					<span
						style={{
							color: "#6F6F6F",
							fontFamily: "Inter, sans-serif",
							fontWeight: 400,
							fontSize: 12,
							lineHeight: "120%",
							letterSpacing: 0,
							opacity: 1
						}}
					>
						Refund for any issue
					</span>
				</div>
			</section>
		</>
	);
};

export default NavbarRefund;
